import { CancelledError } from '@tanstack/react-query';
import type { CatalogPropertyPreview } from 'src/api/generated';
import type {
  PropertyPreviewBatch,
  PropertyPreviewCoordinator,
  PropertyPreviewCoordinatorOptions,
  PropertyPreviewPendingRequest,
} from '../types/property-preview.type';

/** Collects staggered scroll arrivals for a bounded window without debounce starvation. */
export const PROPERTY_PREVIEW_BATCH_WINDOW_MS = 250;
/** Submitted IDs per catalog HTTP request. */
export const PROPERTY_PREVIEW_BATCH_SIZE = 50;
/** Every HTTP attempt, including retries, shares this mounted-view limit. */
export const PROPERTY_PREVIEW_HTTP_CONCURRENCY = 4;

/**
 * Creates the communications view's bounded scheduler and batch retry lifecycle.
 * It stores outstanding work only; the existing QueryClient owns all responses.
 * @param options - Generated API transport, current-context guard and existing API policy.
 * @returns A coordinator that deduplicates pending properties and settles cancellation.
 * @example
 * const coordinator = createPropertyPreviewCoordinator(options);
 * const preview = await coordinator.load(42, querySignal);
 */
export const createPropertyPreviewCoordinator = (
  options: PropertyPreviewCoordinatorOptions
): PropertyPreviewCoordinator => {
  const pending = new Map<number, PropertyPreviewPendingRequest>();
  const collecting = new Set<PropertyPreviewPendingRequest>();
  const batches = new Set<PropertyPreviewBatch>();
  const ready: PropertyPreviewBatch[] = [];
  let collectionTimer: ReturnType<typeof setTimeout> | undefined;
  let activeRequests = 0;
  let disposed = false;

  const isCurrent = (): boolean => !disposed && options.isCurrent();

  const cleanEntry = (entry: PropertyPreviewPendingRequest): void => {
    pending.delete(entry.propertyId);
    collecting.delete(entry);
    entry.batch?.entries.delete(entry);
    for (const cleanup of entry.cleanups) cleanup();
    entry.cleanups.clear();
  };

  const finishBatch = (batch: PropertyPreviewBatch): void => {
    batch.finished = true;
    batches.delete(batch);
    if (batch.retryTimer !== undefined) clearTimeout(batch.retryTimer);
    batch.retryTimer = undefined;
  };

  const cancelEntry = (entry: PropertyPreviewPendingRequest): void => {
    const batch = entry.batch;
    cleanEntry(entry);
    entry.reject(new CancelledError({ revert: true }));
    if (batch && batch.entries.size === 0) {
      finishBatch(batch);
      batch.controller?.abort();
    }
    if (collecting.size === 0 && collectionTimer !== undefined) {
      clearTimeout(collectionTimer);
      collectionTimer = undefined;
    }
  };

  const cancelBatch = (batch: PropertyPreviewBatch): void => {
    finishBatch(batch);
    for (const entry of [...batch.entries]) cancelEntry(entry);
    batch.controller?.abort();
  };

  const terminalFailure = (
    batch: PropertyPreviewBatch,
    error: unknown
  ): void => {
    const propertyIds = [...batch.entries].map((entry) => entry.propertyId);
    finishBatch(batch);
    for (const entry of [...batch.entries]) {
      cleanEntry(entry);
      entry.reject(error);
    }
    // Policy promises never hold query promises open or replace their original error.
    void Promise.resolve()
      .then(() => {
        if (isCurrent()) return options.onTerminalError(error, propertyIds);
      })
      .catch(options.onPolicyError);
  };

  const handleFailure = (batch: PropertyPreviewBatch, error: unknown): void => {
    if (!isCurrent() || batch.finished || batch.controller?.signal.aborted) {
      cancelBatch(batch);
      return;
    }

    const classificationError =
      error instanceof Error
        ? error
        : new Error('Property preview request failed');
    if (
      options.retryPolicy.shouldRetry(
        batch.failureCount,
        classificationError,
        isCurrent
      )
    ) {
      const delay = options.retryPolicy.retryDelay(batch.failureCount);
      batch.failureCount += 1;
      batch.retryTimer = setTimeout(() => {
        batch.retryTimer = undefined;
        if (!isCurrent() || batch.finished) {
          cancelBatch(batch);
          return;
        }
        ready.push(batch);
        pump();
      }, delay);
      return;
    }

    terminalFailure(batch, error);
  };

  const runAttempt = async (batch: PropertyPreviewBatch): Promise<void> => {
    const controller = new AbortController();
    batch.controller = controller;
    activeRequests += 1;
    try {
      const propertyIds = [...batch.entries].map((entry) => entry.propertyId);
      const response = await options.request(propertyIds, controller.signal);
      if (!isCurrent() || batch.finished || controller.signal.aborted) {
        cancelBatch(batch);
        return;
      }

      const results = new Map(
        response.items.map((preview) => [preview.propertyId, preview])
      );
      // A malformed/incomplete response is a failure, never successful absence.
      if ([...batch.entries].some((entry) => !results.has(entry.propertyId))) {
        throw new Error(
          'Property preview response omitted a requested property'
        );
      }

      finishBatch(batch);
      for (const entry of [...batch.entries]) {
        const preview = results.get(entry.propertyId);
        if (preview) {
          cleanEntry(entry);
          entry.resolve(preview);
        }
      }
    } catch (error) {
      handleFailure(batch, error);
    } finally {
      batch.controller = undefined;
      activeRequests -= 1;
      pump();
    }
  };

  const pump = (): void => {
    while (activeRequests < PROPERTY_PREVIEW_HTTP_CONCURRENCY && ready.length) {
      const batch = ready.shift();
      if (!batch || batch.finished) continue;
      if (!isCurrent()) {
        cancelBatch(batch);
        continue;
      }
      void runAttempt(batch);
    }
  };

  const flush = (): void => {
    if (collectionTimer !== undefined) clearTimeout(collectionTimer);
    collectionTimer = undefined;
    if (!isCurrent()) {
      for (const entry of [...collecting]) cancelEntry(entry);
      return;
    }
    const entries = new Set(
      [...collecting].slice(0, PROPERTY_PREVIEW_BATCH_SIZE)
    );
    if (entries.size === 0) return;
    const batch: PropertyPreviewBatch = {
      entries,
      failureCount: 0,
      finished: false,
    };
    for (const entry of entries) {
      collecting.delete(entry);
      entry.batch = batch;
    }
    batches.add(batch);
    ready.push(batch);
    pump();
  };

  const watchAbort = (
    entry: PropertyPreviewPendingRequest,
    signal: AbortSignal
  ): void => {
    const cancel = (): void => cancelEntry(entry);
    signal.addEventListener('abort', cancel, { once: true });
    entry.cleanups.add(() => signal.removeEventListener('abort', cancel));
  };

  const load = (
    propertyId: number,
    signal: AbortSignal
  ): Promise<CatalogPropertyPreview> => {
    if (!isCurrent() || signal.aborted) {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- TanStack cancellation uses its public CancelledError class.
      return Promise.reject(new CancelledError({ revert: true }));
    }
    const existing = pending.get(propertyId);
    if (existing) {
      watchAbort(existing, signal);
      return existing.promise;
    }

    let resolveEntry: (preview: CatalogPropertyPreview) => void = () =>
      undefined;
    let rejectEntry: (error: unknown) => void = () => undefined;
    const promise = new Promise<CatalogPropertyPreview>((resolve, reject) => {
      resolveEntry = resolve;
      rejectEntry = reject;
    });
    const entry: PropertyPreviewPendingRequest = {
      propertyId,
      promise,
      resolve: resolveEntry,
      reject: rejectEntry,
      cleanups: new Set(),
    };
    pending.set(propertyId, entry);
    collecting.add(entry);
    watchAbort(entry, signal);
    if (collecting.size === PROPERTY_PREVIEW_BATCH_SIZE) {
      flush();
    } else {
      collectionTimer ??= setTimeout(flush, PROPERTY_PREVIEW_BATCH_WINDOW_MS);
    }
    return promise;
  };

  const dispose = (): void => {
    disposed = true;
    if (collectionTimer !== undefined) clearTimeout(collectionTimer);
    collectionTimer = undefined;
    for (const entry of [...pending.values()]) cancelEntry(entry);
    for (const batch of [...batches]) cancelBatch(batch);
    ready.length = 0;
  };

  return { load, dispose };
};
